// ============================================================================
// First-login company onboarding — data + pure helpers.
//
// A brand-new owner is asked for their activity type and billing details once
// (components/CompanyOnboarding.jsx); the answers land in the same `profiles`
// columns Configuración › Datos de empresa edits, so they print on documents
// straight away.
//
// Documents print ONE company address line (profiles.address), so the form's
// street / CP / city / province are composed into it. The structured parts are
// ALSO stored (city, postal_code, province) once backend/company_onboarding.sql
// is applied — Facturae needs them as separate fields.
// ============================================================================

export const ACTIVITY_TYPES = ['autonomo', 'empresa', 'asesoria'];

// profiles.business_type drives the tax models (lib/taxModels.js): 'pyme' = S.L.
// An asesoría can be either, so its business_type is left untouched.
const BUSINESS_TYPE_FOR = { autonomo: 'autónomo', empresa: 'pyme' };

export const COUNTRIES = [
  { code: 'ES', es: 'España', ca: 'Espanya', en: 'Spain' },
  { code: 'PK', es: 'Pakistán', ca: 'Pakistan', en: 'Pakistan' },
];

// Spanish provinces indexed by their INE code, which is also the first two
// digits of every postal code in the province (08xxx = Barcelona).
const ES_PROVINCE_BY_INE = {
  '01': 'Álava', '02': 'Albacete', '03': 'Alicante', '04': 'Almería', '05': 'Ávila',
  '06': 'Badajoz', '07': 'Illes Balears', '08': 'Barcelona', '09': 'Burgos', '10': 'Cáceres',
  '11': 'Cádiz', '12': 'Castellón', '13': 'Ciudad Real', '14': 'Córdoba', '15': 'A Coruña',
  '16': 'Cuenca', '17': 'Girona', '18': 'Granada', '19': 'Guadalajara', '20': 'Gipuzkoa',
  '21': 'Huelva', '22': 'Huesca', '23': 'Jaén', '24': 'León', '25': 'Lleida',
  '26': 'La Rioja', '27': 'Lugo', '28': 'Madrid', '29': 'Málaga', '30': 'Murcia',
  '31': 'Navarra', '32': 'Ourense', '33': 'Asturias', '34': 'Palencia', '35': 'Las Palmas',
  '36': 'Pontevedra', '37': 'Salamanca', '38': 'Santa Cruz de Tenerife', '39': 'Cantabria', '40': 'Segovia',
  '41': 'Sevilla', '42': 'Soria', '43': 'Tarragona', '44': 'Teruel', '45': 'Toledo',
  '46': 'Valencia', '47': 'Valladolid', '48': 'Bizkaia', '49': 'Zamora', '50': 'Zaragoza',
  '51': 'Ceuta', '52': 'Melilla',
};

export const ES_PROVINCES = Object.values(ES_PROVINCE_BY_INE).sort((a, b) => a.localeCompare(b, 'es'));

export const PK_PROVINCES = [
  'Punjab', 'Sindh', 'Khyber Pakhtunkhwa', 'Balochistan',
  'Islamabad Capital Territory', 'Gilgit-Baltistan', 'Azad Jammu and Kashmir',
];

export const provincesFor = (country) => (country === 'PK' ? PK_PROVINCES : ES_PROVINCES);

/** Spanish province for a 5-digit postal code, or '' when it isn't one. */
export function provinceFromPostalCode(postalCode) {
  const cp = String(postalCode || '').trim();
  if (!/^\d{5}$/.test(cp)) return '';
  return ES_PROVINCE_BY_INE[cp.slice(0, 2)] || '';
}

// Sector → categories. Labels are stored as text (profiles.sector / category are
// free-text columns shown as-is in Datos de empresa), in the UI language.
const c = (id, es, ca, en) => ({ id, es, ca, en });
export const SECTORS = [
  { ...c('retail', 'Comercio', 'Comerç', 'Retail'), categories: [
    c('food', 'Alimentación y supermercado', 'Alimentació i supermercat', 'Grocery & supermarket'),
    c('fruit', 'Frutería', 'Fruiteria', 'Greengrocer'),
    c('clothing', 'Ropa y calzado', 'Roba i calçat', 'Clothing & footwear'),
    c('electronics', 'Electrónica e informática', 'Electrònica i informàtica', 'Electronics & computers'),
    c('bazaar', 'Bazar y regalos', 'Basar i regals', 'Bazaar & gifts'),
    c('phones', 'Telefonía y locutorio', 'Telefonia i locutori', 'Phones & call shop'),
  ] },
  { ...c('wholesale', 'Distribución y mayorista', 'Distribució i majorista', 'Wholesale & distribution'), categories: [
    c('food', 'Alimentación y bebidas', 'Alimentació i begudes', 'Food & drinks'),
    c('materials', 'Materiales de construcción', 'Materials de construcció', 'Building materials'),
    c('goods', 'Otros productos', 'Altres productes', 'Other goods'),
  ] },
  { ...c('hospitality', 'Hostelería', 'Hostaleria', 'Hospitality'), categories: [
    c('restaurant', 'Restaurante', 'Restaurant', 'Restaurant'),
    c('bar', 'Bar y cafetería', 'Bar i cafeteria', 'Bar & café'),
    c('hotel', 'Hotel y alojamiento', 'Hotel i allotjament', 'Hotel & lodging'),
    c('takeaway', 'Comida para llevar', 'Menjar per emportar', 'Takeaway'),
    c('catering', 'Catering', 'Càtering', 'Catering'),
  ] },
  { ...c('construction', 'Construcción y reformas', 'Construcció i reformes', 'Construction & renovation'), categories: [
    c('building', 'Albañilería', 'Paleta', 'Masonry'),
    c('electrical', 'Electricidad', 'Electricitat', 'Electrical'),
    c('plumbing', 'Fontanería', 'Lampisteria', 'Plumbing'),
    c('painting', 'Pintura', 'Pintura', 'Painting'),
    c('carpentry', 'Carpintería', 'Fusteria', 'Carpentry'),
    c('renovation', 'Reformas integrales', 'Reformes integrals', 'Full renovations'),
  ] },
  { ...c('professional', 'Servicios profesionales', 'Serveis professionals', 'Professional services'), categories: [
    c('consulting', 'Consultoría', 'Consultoria', 'Consulting'),
    c('design', 'Diseño y publicidad', 'Disseny i publicitat', 'Design & advertising'),
    c('architecture', 'Arquitectura e ingeniería', 'Arquitectura i enginyeria', 'Architecture & engineering'),
    c('legal', 'Abogacía', 'Advocacia', 'Legal'),
    c('translation', 'Traducción', 'Traducció', 'Translation'),
    c('photo', 'Fotografía y vídeo', 'Fotografia i vídeo', 'Photo & video'),
  ] },
  { ...c('accounting', 'Asesoría y gestoría', 'Assessoria i gestoria', 'Accounting & advisory'), categories: [
    c('tax', 'Fiscal y contable', 'Fiscal i comptable', 'Tax & accounting'),
    c('labour', 'Laboral', 'Laboral', 'Payroll & labour'),
    c('legal', 'Jurídica', 'Jurídica', 'Legal'),
  ] },
  { ...c('tech', 'Tecnología', 'Tecnologia', 'Technology'), categories: [
    c('software', 'Desarrollo de software', 'Desenvolupament de programari', 'Software development'),
    c('repair', 'Reparación informática', 'Reparació informàtica', 'Computer repair'),
    c('telecom', 'Telecomunicaciones', 'Telecomunicacions', 'Telecommunications'),
    c('marketing', 'Marketing digital', 'Màrqueting digital', 'Digital marketing'),
  ] },
  { ...c('transport', 'Transporte y logística', 'Transport i logística', 'Transport & logistics'), categories: [
    c('freight', 'Transporte de mercancías', 'Transport de mercaderies', 'Freight'),
    c('taxi', 'Taxi y VTC', 'Taxi i VTC', 'Taxi & ride-hailing'),
    c('courier', 'Mensajería', 'Missatgeria', 'Courier'),
    c('removals', 'Mudanzas', 'Mudances', 'Removals'),
  ] },
  { ...c('automotive', 'Automoción', 'Automoció', 'Automotive'), categories: [
    c('workshop', 'Taller mecánico', 'Taller mecànic', 'Car repair shop'),
    c('sales', 'Venta de vehículos', 'Venda de vehicles', 'Vehicle sales'),
    c('parts', 'Recambios', 'Recanvis', 'Spare parts'),
  ] },
  { ...c('health', 'Salud y bienestar', 'Salut i benestar', 'Health & wellness'), categories: [
    c('clinic', 'Clínica', 'Clínica', 'Clinic'),
    c('physio', 'Fisioterapia', 'Fisioteràpia', 'Physiotherapy'),
    c('pharmacy', 'Farmacia', 'Farmàcia', 'Pharmacy'),
    c('optics', 'Óptica', 'Òptica', 'Optician'),
    c('gym', 'Gimnasio', 'Gimnàs', 'Gym'),
  ] },
  { ...c('beauty', 'Belleza y estética', 'Bellesa i estètica', 'Beauty'), categories: [
    c('hair', 'Peluquería', 'Perruqueria', 'Hairdresser'),
    c('barber', 'Barbería', 'Barberia', 'Barber'),
    c('aesthetics', 'Centro de estética', "Centre d'estètica", 'Beauty salon'),
  ] },
  { ...c('education', 'Educación y formación', 'Educació i formació', 'Education & training'), categories: [
    c('academy', 'Academia', 'Acadèmia', 'Academy'),
    c('tutoring', 'Clases particulares', 'Classes particulars', 'Private tutoring'),
    c('driving', 'Autoescuela', 'Autoescola', 'Driving school'),
  ] },
  { ...c('realestate', 'Inmobiliaria', 'Immobiliària', 'Real estate'), categories: [
    c('agency', 'Agencia inmobiliaria', 'Agència immobiliària', 'Estate agency'),
    c('rental', 'Alquiler', 'Lloguer', 'Rentals'),
    c('admin', 'Administración de fincas', 'Administració de finques', 'Property management'),
  ] },
  { ...c('industry', 'Industria y fabricación', 'Indústria i fabricació', 'Manufacturing'), categories: [] },
  { ...c('agriculture', 'Agricultura y ganadería', 'Agricultura i ramaderia', 'Agriculture & farming'), categories: [] },
  { ...c('other', 'Otros', 'Altres', 'Other'), categories: [] },
];

const OTHER_CATEGORY = c('other', 'Otra', 'Una altra', 'Other');

/** Categories offered for a sector id (each non-empty list ends with "Otra"). */
export function categoriesFor(sectorId) {
  const s = SECTORS.find((x) => x.id === sectorId);
  return s && s.categories.length ? [...s.categories, OTHER_CATEGORY] : [];
}

/** es/ca/en label of a data item; anything else reads English (PK = en). */
export const labelOf = (item, lang) => (item ? item[lang] || item.en : '');

// ---------------------------------------------------------------------------
// Spanish NIF / NIE / CIF check digits (AEAT algorithm).
// ---------------------------------------------------------------------------
const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';

export const normalizeTaxId = (id) => String(id || '').toUpperCase().replace(/[\s.\-/]/g, '').replace(/^ES(?=[0-9A-Z]{9}$)/, '');

export function isValidSpanishTaxId(raw) {
  const id = normalizeTaxId(raw);
  // DNI: 8 digits + letter.
  if (/^\d{8}[A-Z]$/.test(id)) return DNI_LETTERS[Number(id.slice(0, 8)) % 23] === id[8];
  // NIE: X/Y/Z + 7 digits + letter (X=0, Y=1, Z=2).
  if (/^[XYZ]\d{7}[A-Z]$/.test(id)) {
    const n = Number('XYZ'.indexOf(id[0]) + id.slice(1, 8));
    return DNI_LETTERS[n % 23] === id[8];
  }
  // K/L/M special NIFs: accepted as-is (rare, no public check we can rely on).
  if (/^[KLM]\d{7}[A-Z0-9]$/.test(id)) return true;
  // CIF: entity letter + 7 digits + control digit or letter.
  if (/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(id)) {
    const d = id.slice(1, 8).split('').map(Number);
    let sum = 0;
    d.forEach((n, i) => {
      if (i % 2 === 1) sum += n; // positions 2, 4, 6
      else { const x = n * 2; sum += Math.floor(x / 10) + (x % 10); }
    });
    const digit = (10 - (sum % 10)) % 10;
    const letter = 'JABCDEFGHI'[digit];
    const ctl = id[8];
    if ('ABEH'.includes(id[0])) return ctl === String(digit);
    if ('NPQRSW'.includes(id[0])) return ctl === letter;
    return ctl === String(digit) || ctl === letter;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Profile patch
// ---------------------------------------------------------------------------

/** "Calle Mayor 1, 08001 Barcelona" (+ ", Province" when it differs from the city). */
export function composeAddress({ street, postalCode, city, province }) {
  const s = (v) => String(v || '').trim();
  const town = [s(postalCode), s(city)].filter(Boolean).join(' ');
  const prov = s(province) && s(province).toLowerCase() !== s(city).toLowerCase() ? s(province) : '';
  return [s(street), town, prov].filter(Boolean).join(', ');
}

/**
 * The `profiles` update for a submitted onboarding form.
 * @param form  { activity, fiscalName, taxId, phone, street, city, postalCode,
 *                province, country, sectorId, categoryId }
 * @param opts  { lang, structured: bool (city/postal_code/province columns exist),
 *                activityColumn: bool (activity_type column exists) }
 */
export function buildProfilePatch(form, { lang = 'es', structured = false, activityColumn = false } = {}) {
  const s = (v) => String(v || '').trim();
  const orNull = (v) => s(v) || null;
  const country = form.country === 'PK' ? 'PK' : 'ES';
  const sector = SECTORS.find((x) => x.id === form.sectorId);
  const category = categoriesFor(form.sectorId).find((x) => x.id === form.categoryId);

  const patch = {
    company_name: s(form.fiscalName),
    cif_nif: country === 'ES' ? normalizeTaxId(form.taxId) : s(form.taxId),
    phone: orNull(form.phone),
    address: orNull(composeAddress(form)),
    sector: sector ? labelOf(sector, lang) : null,
    category: category ? labelOf(category, lang) : null,
    country_code: country,
  };
  const bt = BUSINESS_TYPE_FOR[form.activity];
  if (bt) patch.business_type = bt;
  if (country === 'PK' && s(form.province)) patch.pk_province = s(form.province);
  if (structured) {
    patch.city = orNull(form.city);
    patch.postal_code = orNull(form.postalCode);
    patch.province = orNull(form.province);
  }
  if (activityColumn && ACTIVITY_TYPES.includes(form.activity)) patch.activity_type = form.activity;
  return patch;
}
